import api from "./api";

export type CameraStatusApiRow = {
  establishmentId: string;
  establishmentName?: string | null;
  deviceId: number;
  deviceName: string;
  errorReasons?: string;
  [key: string]: unknown;
};

export type CameraStatusApiResponse = {
  data?: CameraStatusApiRow[];
  errors?: Array<{ establishmentId: string; message: string }>;
};

const CACHE_TTL_MS = 2500;

const inFlightByKey = new Map<string, Promise<CameraStatusApiResponse>>();
const cacheByKey = new Map<
  string,
  {
    value: CameraStatusApiResponse;
    expiresAt: number;
  }
>();

export async function fetchCameraStatusShared(options?: {
  force?: boolean;
  establishmentIds?: string[];
}): Promise<CameraStatusApiResponse> {
  const force = !!options?.force;
  const establishmentIds = Array.from(
    new Set(
      (options?.establishmentIds ?? [])
        .map((id) => String(id ?? "").trim())
        .filter(Boolean),
    ),
  ).sort();
  const key = establishmentIds.length ? establishmentIds.join(",") : "__all__";
  const now = Date.now();
  const cached = cacheByKey.get(key);

  if (!force && cached && cached.expiresAt > now) {
    return cached.value;
  }

  const inFlight = inFlightByKey.get(key);
  if (!force && inFlight) {
    return inFlight;
  }

  const request = api
    .get<CameraStatusApiResponse>("/app/cameras/status", {
      params: establishmentIds.length
        ? { establishmentIds: establishmentIds.join(",") }
        : undefined,
    })
    .then((response) => {
      const value = response.data ?? { data: [], errors: [] };
      cacheByKey.set(key, {
        value,
        expiresAt: Date.now() + CACHE_TTL_MS,
      });
      return value;
    })
    .finally(() => {
      inFlightByKey.delete(key);
    });

  inFlightByKey.set(key, request);
  return request;
}
